import { call, put, all, select } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { getProductVersions, retrievePlatformId, retrieveProductData } from 'src/services/productClient';
import { retrieveAllAndRequiredVariables } from 'src/services/attributeClient';
import { getSkuById } from 'src/selectors/skus';
import { logError } from 'src/loggingManager';
import { PlatformIdResult, ProductData, AttributeRulesResult } from 'src/types/services';
import {
    fetchSku,
    fetchSkuSuccess,
    fetchSkuFailed,
    retrieveRules,
    retrieveRulesSuccess,
    retrieveRulesFailed,
    fetchVersionsForSkuSuccess,
    fetchVersions,
    fetchVersionsForSkuFailed,
} from './slice';

type TranslateSkuParams = {
    sku: string;
    skuVersion?: string | number;
    attributes?: Record<string, unknown>;
};

type TranslateSkuResult = {
    platformId: string;
    platformIdVersion?: string | number;
    platformIdAttributes?: Record<string, unknown>;
    merchantId?: string;
};

type FetchProductPayload = {
    sku: string;
    skuVersion?: string | number;
    throwAgain?: boolean;
};

type FetchProductsPayload = {
    skus: string[];
};

type RetrieveRulesPayload = {
    sku: string;
    skuVersion?: string | number;
};

/**
 * Translates a PRD SKU to platform ID or returns the SKU itself
 */
export function* translateSku({
    sku,
    skuVersion,
    attributes,
}: TranslateSkuParams): Generator<any, TranslateSkuResult, any> {
    if (sku.startsWith('PRD')) {
        try {
            const numericSkuVersion = typeof skuVersion === 'string' ? parseInt(skuVersion, 10) : skuVersion;
            const { merchantSku, merchantVersion, merchantVariables } = (yield* call(retrievePlatformId, {
                sku,
                skuVersion: numericSkuVersion,
                attributes: attributes || {},
            })) as PlatformIdResult;

            return {
                platformId: merchantSku,
                platformIdVersion: merchantVersion,
                platformIdAttributes: merchantVariables,
            };
        } catch (error: any) {
            logError(`Failed to retrieve the VP platform id: ${sku} ${error}`);
            throw error;
        }
    }

    return { platformId: sku, merchantId: sku };
}

/**
 * Fetches product data for a SKU
 */
export function* fetchProduct({ payload }: PayloadAction<FetchProductPayload>): Generator<any, void, any> {
    const { sku, skuVersion, throwAgain } = payload;
    const product = yield* select(getSkuById, sku);
    if (product) {
        if (product.doesNotExist && throwAgain) {
            throw Error('Sku has been searched for already and does not exist!');
        }
    }

    const numericSkuVersion = typeof skuVersion === 'string' ? parseInt(skuVersion, 10) : skuVersion;
    yield* put(fetchSku({ sku, skuVersion: numericSkuVersion }));
    try {
        const productData = (yield* call(retrieveProductData, { sku, skuVersion: numericSkuVersion })) as ProductData;
        yield* put(
            fetchSkuSuccess({
                sku,
                skuVersion: numericSkuVersion,
                productName: productData.name,
                lastModified: productData.modified,
                options: productData.options,
            }),
        );
    } catch (error: any) {
        yield* put(fetchSkuFailed({ sku, skuVersion: numericSkuVersion }));
        if (throwAgain) {
            throw error;
        }
    }
}

/**
 * Loads available versions for a SKU
 */
export function* loadVersions({ payload }: PayloadAction<string>): Generator<any, void, any> {
    const product = yield* select(getSkuById, payload);

    if (product) {
        if (product.doesNotExist) {
            throw Error('Sku has been searched for already and does not exist!');
        }
        return;
    }

    try {
        yield* put(fetchVersions({ sku: payload }));
        const versions = yield* call(getProductVersions, payload);
        yield* put(fetchVersionsForSkuSuccess({ versions, sku: payload }));
    } catch (error: any) {
        try {
            const isV1Product = (yield* call(retrieveProductData, { sku: payload })) as ProductData;
            if (isV1Product.name) {
                yield* put(fetchVersionsForSkuSuccess({ versions: [], sku: payload, isV1Product: true }));
            }
        } catch {
            yield* put(fetchVersionsForSkuFailed(payload));
        }
    }
}

/**
 * Fetches multiple products in parallel
 */
export function* fetchProducts({ payload }: PayloadAction<FetchProductsPayload>): Generator<any, void, any> {
    yield* all(payload.skus.map((sku) => call(fetchProduct, { payload: { sku }, type: 'skus/fetchProduct' } as any)));
}

/**
 * Retrieves attribute rules for a product
 */
export function* retrieveRulesForProduct({ payload }: PayloadAction<RetrieveRulesPayload>): Generator<any, void, any> {
    const { sku, skuVersion } = payload;
    const skuKey = skuVersion ? `${sku}-${skuVersion}` : sku;
    const skuData = yield* select(getSkuById, skuKey);
    if (skuData && skuData.rulesLoaded) {
        return;
    }

    yield* put(retrieveRules({ sku, skuVersion }));
    try {
        const { ruleSet, requiredVariables, merchandisingOnly } = (yield* call(
            retrieveAllAndRequiredVariables,
            sku,
        )) as AttributeRulesResult;
        yield* put(retrieveRulesSuccess({ sku, skuVersion, requiredVariables, ruleSet, merchandisingOnly }));
    } catch (error: any) {
        logError(`rules retrieval error ${sku} v${skuVersion} ${error}`);
        yield* put(retrieveRulesFailed({ sku, skuVersion }));
    }
}
